import React, { useEffect, useMemo, useState } from 'react'
import type { MutableRefObject } from 'react'
import type { MessageDescriptor } from 'react-intl'
import { ScrollView } from 'react-native'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { useTheme } from 'styled-components/native'

import type { AppointmentSummary } from '@lyrahealth-inc/shared-app-logic/src/models/appointmentSummary/AppointmentSummary'
import { NoteTypes } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'
import { MenuIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/MenuIcon'
import { SideNavMessageIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SideNavMessageIcon'
import { SparkleGradient } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SparkleGradient'
import { ProfileCard } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/profileCard/ProfileCard'
import { TabbedExpandingSidePanelTabItem } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/sidePanel/TabbedExpandingSidePanel'
import { TabIconWithTooltip } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/tabIconWithTooltip/TabIconWithTooltip'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { RiskBadgeForClient } from '../../../common/components/riskBadge/RiskBadgeWithPopover'
import { CLIENT_HOME } from '../../../common/constants/routingConstants'
import { getAuthConfig } from '../../../data/auth/authSelectors'
import { useIsEngageDAProvider } from '../../../hooks/useIsEngageDAProvider'
import { useAssignmentsData } from '../../assignments/data/hooks/useAssignmentsData'
import { useClientAppointmentsData } from '../../clients/clientDetails/data/hooks/useClientAppointmentsData'
import { useSelectedClientData } from '../../clients/clientDetails/data/hooks/useSelectedClientData'
import { useSelectedEpisodeId } from '../../clients/clientDetails/hooks/useSelectedEpisodeId'
import { useEpisodesForSelectedClient } from '../../episodes/data/hooks/useEpisodesForSelectedClient'
import BaseChat, { ChatEntryPoint } from '../../messages/BaseChat'
import useDetailsPanelData from '../../messages/data/useDetailsPanelData'
import ClientNotesPanel from '../ClientNotesPanel'

export const SIDEBAR_TRANSITION_TIME = 300

type SidePanelToggleTypes = NoteTypes | 'EndOfSession'

export const PANEL_TAB_IDS = {
  AI_NOTES: 'ai-notes',
  CLIENT_DETAILS: 'client-details',
  MESSAGES: 'messages',
}

const AI_NOTES_NOTE_TYPES = new Set<SidePanelToggleTypes>([NoteTypes.SESSION, NoteTypes.INITIAL])

const CLIENT_DETAILS_NOTE_TYPES = new Set<SidePanelToggleTypes>([
  NoteTypes.SESSION,
  NoteTypes.INITIAL,
  NoteTypes.GENERAL,
  NoteTypes.REFERRAL,
  'EndOfSession',
])

const MESSAGES_NOTE_TYPES = new Set<SidePanelToggleTypes>([NoteTypes.SESSION, NoteTypes.INITIAL, NoteTypes.REFERRAL])

type AIClientNotesPanelProps = {
  appointmentId: string
  feedbackFormRef: MutableRefObject<any>
  fieldName: string
  noteAppointmentSummary: AppointmentSummary
  noteId?: string
  noteRef: MutableRefObject<any>
  noteType: string
  panelTitle: MessageDescriptor | string
  promptType: string
}

export const useGetTabbedExpandingSidePanelContent = ({
  showAISummary = false,
  noteType,
  aiPanelProps,
}: {
  showAISummary?: boolean
  noteType: SidePanelToggleTypes
  aiPanelProps?: AIClientNotesPanelProps
}) => {
  const { colors } = useTheme()
  const [selectedEpisodeId] = useSelectedEpisodeId()
  const { data: selectedClientDetails, isLoading: isLoadingClientDetails } = useSelectedClientData()
  const { data: episodes } = useEpisodesForSelectedClient()
  const { data: appointments } = useClientAppointmentsData()
  const { data: assignments } = useAssignmentsData()
  const navigate = useNavigate()
  const isEngageDAProvider = useIsEngageDAProvider()
  const hideRiskAlerts = useSelector(getAuthConfig)?.hideRiskAlerts
  const selectedClientId = selectedClientDetails?.id

  const clientDetails = useDetailsPanelData({
    client: selectedClientDetails ?? null,
    episodes,
    isEngageDAProvider,
    appointments,
    assignments,
  })

  const tabs: TabbedExpandingSidePanelTabItem[] = useMemo(() => {
    const t: TabbedExpandingSidePanelTabItem[] = []
    if (showAISummary && noteType && AI_NOTES_NOTE_TYPES.has(noteType) && aiPanelProps) {
      t.push({
        id: PANEL_TAB_IDS.AI_NOTES,
        icon: (isActive: boolean, isHovered?: boolean) => (
          <TabIconWithTooltip
            icon={<SparkleGradient size={24} color={isActive ? colors.iconActive : colors.iconDefault} />}
            tooltipText='AI Notes'
            testID={tID(`tab-icon-${PANEL_TAB_IDS.AI_NOTES}`)}
            isHovered={isHovered}
          />
        ),
        content: (
          <ClientNotesPanel
            appointmentId={aiPanelProps.appointmentId}
            feedbackFormRef={aiPanelProps.feedbackFormRef}
            fieldName={aiPanelProps.fieldName}
            noteAppointmentSummary={aiPanelProps.noteAppointmentSummary}
            noteId={aiPanelProps.noteId}
            noteRef={aiPanelProps.noteRef}
            panelTitle={aiPanelProps.panelTitle}
            noteType={aiPanelProps.noteType}
            promptType={aiPanelProps.promptType}
            customContainerStyle={{ padding: '16px 0' }}
          />
        ),
      })
    }
    if (noteType && CLIENT_DETAILS_NOTE_TYPES.has(noteType)) {
      t.push({
        id: PANEL_TAB_IDS.CLIENT_DETAILS,
        icon: (isActive: boolean, isHovered?: boolean) => (
          <TabIconWithTooltip
            icon={<MenuIcon size={24} fillColor={isActive ? colors.iconActive : colors.iconDefault} />}
            tooltipText='Client details'
            testID={tID(`tab-icon-${PANEL_TAB_IDS.CLIENT_DETAILS}`)}
            isHovered={isHovered}
          />
        ),
        content: (
          <ScrollView>
            <ProfileCard
              isLoading={isLoadingClientDetails}
              clientDetails={clientDetails}
              onOpenProfile={() => navigate(CLIENT_HOME.route)}
              withBorder={false}
              hasClientDetailsPageInformation={false}
              showGuardianInfo
              withoutHorizontalOrientation
              riskBadgeSlot={
                !hideRiskAlerts && selectedClientId ? (
                  <RiskBadgeForClient
                    clientId={selectedClientId}
                    logNoteSource='clientNotes'
                    showRiskBadge={clientDetails?.client.showRiskBadge}
                  />
                ) : undefined
              }
            />
          </ScrollView>
        ),
      })
    }
    if (noteType && MESSAGES_NOTE_TYPES.has(noteType)) {
      t.push({
        id: PANEL_TAB_IDS.MESSAGES,
        icon: (isActive: boolean, isHovered?: boolean) => (
          <TabIconWithTooltip
            icon={
              <SideNavMessageIcon
                unreadMessageCount={0}
                fillColor={isActive ? colors.iconActive : colors.iconDefault}
              />
            }
            tooltipText='Messages'
            testID={tID(`tab-icon-${PANEL_TAB_IDS.MESSAGES}`)}
            isHovered={isHovered}
          />
        ),
        content: (
          <BaseChat
            hideChatHeader
            expandedButton
            entryPoint={ChatEntryPoint.CLIENT_NOTES}
            selectedEpisodeId={selectedEpisodeId}
          />
        ),
      })
    }
    return t
  }, [
    showAISummary,
    noteType,
    aiPanelProps,
    colors.iconActive,
    colors.iconDefault,
    isLoadingClientDetails,
    clientDetails,
    selectedClientId,
    hideRiskAlerts,
    navigate,
    selectedEpisodeId,
  ])
  const [selectedTab, setSelectedTab] = useState<string>(tabs[0]?.id)
  const [hoveredTabId, setHoveredTabId] = useState<string | null>(null)

  useEffect(() => {
    const firstId = tabs[0]?.id
    if (firstId) {
      setSelectedTab(firstId)
    }
  }, [tabs])

  return { tabs, selectedTab, setSelectedTab, hoveredTabId, setHoveredTabId }
}
